import { useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
} from "reactstrap";

import { apiSlice } from "app/apiSlice";
import { showToast } from "components/Toasts/helpers/showToast";
import {
  type IndividualPaymentModalProps,
  type MilestonePayment,
} from "../types";
import { formatAccountingAmount } from "../utils/formatters";

export const IndividualPaymentModal = ({
  isOpen,
  onClose,
  milestone,
}: IndividualPaymentModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [payMilestones] = apiSlice.usePayMilestonesMutation();

  const amount = useMemo(() => {
    if (!milestone) return 0;
    const val = Number(milestone.holdBalance ?? milestone.rate ?? 0);
    return Number.isFinite(val) ? val : 0;
  }, [milestone]);

  const handleConfirmPayment = async () => {
    if (!milestone?._id) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await payMilestones({ milestoneIds: [milestone._id] }).unwrap();

      showToast({
        type: "success",
        message: `Successfully initiated payment for milestone: ${milestone.name || milestone.title || "Unnamed Milestone"}`,
      });
      onClose();
    } catch (err: any) {
      setError(
        err?.data?.message ||
          err?.message ||
          "Failed to process payment. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!milestone) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>Confirm Payment</ModalHeader>
      <ModalBody>
        {error && (
          <Alert color="danger" className="mb-3">
            <strong>Payment Failed:</strong> {error}
          </Alert>
        )}

        <div className="mb-3">
          <h6>Payment Confirmation</h6>
          <p className="mb-3">Are you sure you want to pay this milestone?</p>
        </div>

        <div className="border rounded p-3 bg-light">
          <div className="row mb-2">
            <div className="col-4 fw-bold">Milestone:</div>
            <div className="col-8">
              {milestone.name || milestone.title || "Unnamed Milestone"}
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-4 fw-bold">Creator:</div>
            <div className="col-8">
              {milestone.user_id?.name || "Unknown Creator"}
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-4 fw-bold">Brand:</div>
            <div className="col-8">
              {milestone.brand_id?.name || "Unknown Brand"}
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-4 fw-bold">Campaign:</div>
            <div className="col-8">
              {milestone.campaign_id?.name || "Unknown Campaign"}
            </div>
          </div>
          <div className="row">
            <div className="col-4 fw-bold">Amount:</div>
            <div className="col-8 fw-bold">
              {formatAccountingAmount(amount)}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleConfirmPayment}
          disabled={isSubmitting || amount <= 0}
        >
          {isSubmitting
            ? "Processing..."
            : `Pay ${formatAccountingAmount(amount)}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
